/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/resourceItem
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { suppressKeyboardEventDisableEnterOnEdit } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { TextWithMetadata } from 'labstep-web/core/DataGrid/Text/WithMetadata';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { TextWithIcon } from 'labstep-web/core/Text/WithIcon';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { getProtocolValueResourceItemStatusSelectProps } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/utils';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import {
  ResourceItem,
  type ResourceItemStatus,
  ResourceItemStatusValues,
} from 'labstep-web/models/resource-item.model';
import styles from './styles.module.scss';

export const getProtocolValueResourceItemStatusColDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => ({
  colId: `resource_item_status`,
  headerName: 'Status',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: {
    toolPanelColumnName: `Status`,
    icon: 'info circle',
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolValueService.getEditable(params),
  suppressKeyboardEvent: suppressKeyboardEventDisableEnterOnEdit,
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValue = params.data as ProtocolValue;

    if (protocolValue?.resourceItem?.status) {
      return protocolValue?.resourceItem?.status;
    }
    return '';
  },
  valueSetter: (params: ValueSetterParams) => {
    if (!params.node || !params.newValue) {
      return false;
    }
    const newValue = params.newValue.toString().toLowerCase();

    const protocolValue = params.data as ProtocolValue;

    if (!protocolValue?.resourceItem) {
      return false;
    }

    if (Object.keys(ResourceItemStatusValues).includes(newValue)) {
      return GridSmartTableProtocolValueService.updateEntity(
        params,
        ResourceItem.entityName,
        protocolValue!.resourceItem!.idAttr,
        {
          status: newValue as ResourceItemStatus,
        },
      );
    }
    return false;
  },

  cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
    if (!params.data) {
      return '';
    }

    const protocolValue = params.data as ProtocolValue;

    if (!protocolValue || !protocolValue?.resourceItem) {
      return null;
    }

    const resourceItem = protocolValue.resourceItem;

    return (
      <TextWithMetadata
        text={
          <Flex className={styles.status}>
            <TextWithIcon
              icon="circle"
              iconProps={{ className: styles[resourceItem.status] }}
              text={
                ResourceItemStatusValues[
                  resourceItem.status as keyof typeof ResourceItemStatusValues
                ]
              }
            />
            <Icon
              name="sort down"
              onClick={() =>
                params.api.startEditingCell({
                  rowIndex: params.rowIndex,
                  colKey: 'status',
                })
              }
            />
          </Flex>
        }
      />
    );
  },
  cellEditor: DataGrid?.CellEditor?.Select,
  cellEditorPopup: true,
  cellEditorParams: (params: CellEditorParams<ProtocolValue>) => {
    if (!params.node) {
      return false;
    }

    const protocolValue = params.data as ProtocolValue;

    if (!protocolValue || !protocolValue?.resourceItem) {
      return false;
    }

    const onPropsChange = (option: { value: string }) => {
      GridSmartTableProtocolValueService.updateEntity(
        params,
        ResourceItem.entityName,
        protocolValue!.resourceItem!.idAttr,
        {
          status: option.value as ResourceItemStatus,
        },
      );
    };

    return {
      props: getProtocolValueResourceItemStatusSelectProps(
        protocolValue.resourceItem,
        onPropsChange,
      ),
    };
  },
  // FIXME
  // cellStyle: (params) =>
  //   GridSmartTableProtocolValueService.getCellStyle(
  //     params,
  //     experimentOrProtocol,
  //     ProtocolValue.entityName,
  //     name,
  //   ),
});
